/**
 * Billing Page
 * Page for managing subscription and invoices
 */

import {
  BillingCycleToggle,
  BillingHistoryTable,
  BillingInvoiceSummary,
  BillingMetricPill,
  BillingStatusBadge,
  BillingSurface,
  CancelSubscriptionContent,
  CurrentPlanCard,
  PackageCard,
  UsageProgress,
  formatBillingDate,
  formatMoney,
  getSubscriptionLabel,
  getSubscriptionTone,
} from '@/renderer/components/billing';
import { ConfirmDialog } from '@/renderer/components/common/ConfirmDialog';
import { Modal } from '@/renderer/components/common/Modal';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/renderer/components/ui';
import { getBillingPlanGuard } from '@/renderer/services/billingPlanGuards';
import {
  getBillingCycleActionLabel,
  getPackageTotalPriceForCycle,
  isPackageCycleConfigured,
} from '@/renderer/services/billingPricing';
import {
  createCheckoutSession,
  previewSubscriptionUpdate,
} from '@/renderer/services/billingService';
import { AppDispatch } from '@/renderer/store';
import { selectCurrentUser } from '@/renderer/store/auth/authSelectors';
import {
  cancelSubscription,
  createBillingPortalSession,
  fetchBilling,
  fetchInvoices,
  fetchPackages,
  fetchRateLimits,
  fetchUsage,
  resumeSubscription,
  selectBillingLoading,
  selectCurrentPackage,
  selectInvoices,
  selectIsCanceled,
  selectIsTrialing,
  selectPlanRateLimits,
  selectSubscription,
  selectSubscriptionPackages,
  selectTrialDaysRemaining,
  selectUpcomingInvoice,
  selectUsage,
  updateSubscriptionPackage,
} from '@/renderer/store/billing';
import type {
  BillingCycle as BillingCycleId,
  SubscriptionPackage as BillingPackageId,
} from '@/renderer/types/billing';
import { SubscriptionPackage } from '@/renderer/types/company';
import { isElectron } from '@/renderer/util';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Building2, Package, ReceiptText, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const toCompanySubscriptionPackage = (packageId: BillingPackageId) => {
  switch (packageId) {
    case 'starter':
      return SubscriptionPackage.STARTER;
    case 'professional':
      return SubscriptionPackage.PROFESSIONAL;
    case 'enterprise':
      return SubscriptionPackage.ENTERPRISE;
    case 'pro_semi_annual':
      return SubscriptionPackage.PRO_SEMI_ANNUAL;
    default:
      return SubscriptionPackage.STARTER;
  }
};

const loadBillingData = (
  dispatch: AppDispatch,
  companyId: number,
  packageType: string,
) =>
  Promise.all([
    dispatch(fetchBilling(companyId)),
    dispatch(fetchPackages()),
    dispatch(fetchInvoices(companyId)),
    dispatch(fetchUsage({ companyId, packageType })),
    dispatch(fetchRateLimits()),
  ]);

const BillingPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector(selectCurrentUser);
  const subscription = useSelector(selectSubscription);
  const packages = useSelector(selectSubscriptionPackages);
  const usage = useSelector(selectUsage);
  const invoices = useSelector(selectInvoices);
  const isLoading = useSelector(selectBillingLoading);
  const currentPackage = useSelector(selectCurrentPackage);
  const isTrialing = useSelector(selectIsTrialing);
  const isCanceled = useSelector(selectIsCanceled);
  const trialDaysRemaining = useSelector(selectTrialDaysRemaining);
  const upcomingInvoice = useSelector(selectUpcomingInvoice);
  const planRateLimits = useSelector(selectPlanRateLimits);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activePackageId, setActivePackageId] = useState<
    (typeof packages)[number]['id'] | null
  >(null);
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycleId>('monthly');
  const [planSwitchPreview, setPlanSwitchPreview] = useState<{
    packageId: BillingPackageId;
    billingCycle: BillingCycleId;
    amountDue: number;
    currency: string;
    periodEnd: number | null;
  } | null>(null);
  const [isSwitchingPlan, setIsSwitchingPlan] = useState(false);

  const companyId = currentUser?.company?.id;
  const packageType =
    subscription?.package || currentUser?.subscription?.package || 'starter';
  const currentBillingCycle = subscription?.billing_cycle || 'monthly';
  const fallbackNextChargeValue =
    currentPackage?.price != null
      ? `${formatMoney(
          getPackageTotalPriceForCycle(currentPackage, currentBillingCycle),
        )}/${currentBillingCycle === 'annual' ? 'year' : currentBillingCycle === 'semi_anually' ? '6 mo' : 'month'}`
      : 'Pending';
  const latestPaidInvoice =
    [...invoices]
      .filter((invoice) => invoice.status === 'paid')
      .sort(
        (left, right) =>
          new Date(right.paid_at || right.invoice_date).getTime() -
          new Date(left.paid_at || left.invoice_date).getTime(),
      )[0] || null;
  const isBillingRecovery =
    Boolean(subscription?.suspended_at) ||
    Boolean(
      subscription?.subscription_status === 'past_due' &&
      subscription?.grace_period_ends_at &&
      new Date(subscription.grace_period_ends_at).getTime() <= Date.now(),
    );
  const isBillingGracePeriod =
    subscription?.subscription_status === 'past_due' && !isBillingRecovery;

  useEffect(() => {
    if (subscription?.billing_cycle) {
      setSelectedBillingCycle(subscription.billing_cycle);
    }
  }, [subscription?.billing_cycle]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    loadBillingData(dispatch, companyId, packageType).catch(() => {
      // Errors are handled by individual thunks.
    });
  }, [companyId, dispatch, packageType]);

  const refreshBillingState = async (nextPackageType = packageType) => {
    if (!companyId) {
      return;
    }

    await loadBillingData(dispatch, companyId, nextPackageType);
  };

  const openExternalUrl = async (url: string) => {
    if (isElectron()) {
      await window.electron.ipcRenderer.invoke('open-external', url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleManageBilling = async () => {
    const result = await dispatch(
      createBillingPortalSession(window.location.href),
    );

    if (createBillingPortalSession.fulfilled.match(result)) {
      await openExternalUrl(result.payload.url);
      return;
    }

    apiError((result.payload as string) || 'Failed to open billing portal');
  };

  const handleCancelSubscription = async () => {
    const result = await dispatch(cancelSubscription(true));

    if (cancelSubscription.fulfilled.match(result)) {
      setShowCancelModal(false);
      apiSuccess(
        'Subscription will remain active until the current period ends.',
      );
      await refreshBillingState();
      return;
    }

    apiError((result.payload as string) || 'Failed to cancel subscription');
  };

  const handleResumeSubscription = async () => {
    const result = await dispatch(resumeSubscription());

    if (resumeSubscription.fulfilled.match(result)) {
      apiSuccess('Subscription resumed successfully.');
      await refreshBillingState();
      return;
    }

    apiError((result.payload as string) || 'Failed to resume subscription');
  };

  const handlePlanAction = async (selectedPackageId: BillingPackageId) => {
    if (!companyId) {
      apiError('Billing is not available for this account yet.');
      return;
    }

    if (
      selectedPackageId === subscription?.package &&
      selectedBillingCycle === currentBillingCycle
    ) {
      await handleManageBilling();
      return;
    }

    const planGuard = getBillingPlanGuard(
      usage,
      selectedPackageId,
      planRateLimits,
    );
    if (!planGuard.isAllowed) {
      apiError(planGuard.message || 'This plan is not available right now.');
      return;
    }

    const selectedPackage = packages.find(
      (pkg) => pkg.id === selectedPackageId,
    );

    if (
      selectedPackage &&
      !isPackageCycleConfigured(selectedPackage, selectedBillingCycle)
    ) {
      apiError(
        `The ${getBillingCycleActionLabel(selectedBillingCycle)} price for ${selectedPackage.name} has not been configured yet.`,
      );
      return;
    }

    setActivePackageId(selectedPackageId);

    try {
      if (subscription?.stripe_subscription_id) {
        const preview = await previewSubscriptionUpdate(
          selectedPackageId,
          selectedBillingCycle,
        );

        if (!preview.success) {
          apiError(preview.error || 'Failed to preview subscription change');
          return;
        }

        setPlanSwitchPreview({
          packageId: selectedPackageId,
          billingCycle: selectedBillingCycle,
          amountDue: preview.amountDue,
          currency: preview.currency,
          periodEnd: preview.periodEnd,
        });
        return;
      }

      const checkoutResult = await createCheckoutSession(
        toCompanySubscriptionPackage(selectedPackageId),
        companyId,
        selectedBillingCycle,
      );

      if (!checkoutResult.success || !checkoutResult.sessionUrl) {
        apiError(
          checkoutResult.error || 'Failed to create Stripe checkout session',
        );
        return;
      }

      await openExternalUrl(checkoutResult.sessionUrl);
    } finally {
      setActivePackageId(null);
    }
  };

  const confirmPlanSwitch = async () => {
    if (!planSwitchPreview) {
      return;
    }

    const { packageId: selectedPackageId, billingCycle } = planSwitchPreview;
    const selectedPackage = packages.find(
      (pkg) => pkg.id === selectedPackageId,
    );

    setIsSwitchingPlan(true);
    try {
      const result = await dispatch(
        updateSubscriptionPackage({
          billingCycle,
          packageId: selectedPackageId,
        }),
      );

      if (!updateSubscriptionPackage.fulfilled.match(result)) {
        apiError((result.payload as string) || 'Failed to update subscription');
        return;
      }

      apiSuccess(
        `${selectedPackage?.name || 'Subscription'} updated to ${getBillingCycleActionLabel(billingCycle)} billing successfully.`,
      );
      setPlanSwitchPreview(null);
      await refreshBillingState(selectedPackageId);
    } finally {
      setIsSwitchingPlan(false);
    }
  };

  const getPlanActionLabel = (packageId: BillingPackageId) => {
    if (
      packageId === subscription?.package &&
      selectedBillingCycle === currentBillingCycle
    ) {
      return 'Manage plan';
    }

    if (
      packageId === subscription?.package &&
      subscription?.stripe_subscription_id
    ) {
      return `Switch to ${getBillingCycleActionLabel(selectedBillingCycle)} billing`;
    }

    const planGuard = getBillingPlanGuard(usage, packageId, planRateLimits);
    if (!planGuard.isAllowed) {
      return 'Unavailable';
    }

    if (!subscription?.stripe_subscription_id) {
      return selectedBillingCycle === 'annual'
        ? 'Start yearly checkout'
        : 'Start checkout';
    }

    const selectedPackage = packages.find((pkg) => pkg.id === packageId);
    if (
      currentPackage &&
      selectedPackage &&
      selectedPackage.display_order < currentPackage.display_order
    ) {
      return 'Switch plan';
    }

    return 'Upgrade plan';
  };

  if (!companyId) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <BillingSurface tone="tinted">
          <h1 className="text-2xl font-semibold text-slate-950">
            Billing unavailable
          </h1>
          <p className="mt-2 max-w-lg text-sm text-slate-600">
            We could not find a company billing profile for this account yet.
          </p>
        </BillingSurface>
      </div>
    );
  }

  if (isLoading && !subscription) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 pb-8 sm:p-6">
      <BillingSurface tone="tinted" className="relative overflow-hidden">
        <div className="absolute -right-8 top-0 h-32 w-32 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-28 w-28 rounded-full bg-white/70 blur-2xl" />

        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <BillingStatusBadge
                label={getSubscriptionLabel(subscription)}
                tone={getSubscriptionTone(subscription)}
              />
              {upcomingInvoice && (
                <BillingStatusBadge
                  label={`Next billing ${formatBillingDate(
                    upcomingInvoice.due_date || upcomingInvoice.invoice_date,
                  )}`}
                  tone="neutral"
                />
              )}
            </div>

            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
              Billing & Subscription
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
              Compact control over plan changes and invoice history, tuned to
              the product&apos;s green visual system.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:w-[28rem]">
            <BillingMetricPill
              label="Current package"
              value={currentPackage?.name || 'Starter'}
              hint="Active workspace subscription"
              icon={<Package size={16} />}
            />
            <BillingMetricPill
              label="Next charge"
              value={
                upcomingInvoice
                  ? formatMoney(upcomingInvoice.amount_due, {
                      currency: upcomingInvoice.currency.toUpperCase(),
                      fromMinorUnits: true,
                    })
                  : fallbackNextChargeValue
              }
              hint={
                upcomingInvoice
                  ? formatBillingDate(
                      upcomingInvoice.due_date || upcomingInvoice.invoice_date,
                    )
                  : 'No upcoming invoice yet'
              }
              icon={<ReceiptText size={16} />}
            />
            <BillingMetricPill
              label="Usage snapshot"
              value={
                usage
                  ? `${usage.users.used} users / ${usage.outlets.used} outlets`
                  : 'Syncing usage'
              }
              hint="Live from the current billing cycle"
              icon={<Users size={16} />}
            />
            <BillingMetricPill
              label="Invoices tracked"
              value={
                invoices.length > 0 ? `${invoices.length} total` : 'None yet'
              }
              hint={
                upcomingInvoice
                  ? `Next due ${formatBillingDate(
                      upcomingInvoice.due_date || upcomingInvoice.invoice_date,
                    )}`
                  : ''
              }
              icon={<ReceiptText size={16} />}
            />
          </div>
        </div>
      </BillingSurface>

      {isBillingGracePeriod ? (
        <div className="rounded-[26px] border border-amber-200 bg-amber-50 px-5 py-4">
          <p className="text-sm font-semibold text-amber-900">
            Billing grace period is active
          </p>
          <p className="mt-1 text-sm text-amber-800">
            Payment is still pending. Please resolve billing before{' '}
            {formatBillingDate(subscription?.grace_period_ends_at)} to avoid
            service interruption.
          </p>
        </div>
      ) : null}

      {isBillingRecovery ? (
        <div className="rounded-[26px] border border-red-200 bg-red-50 px-5 py-4">
          <p className="text-sm font-semibold text-red-900">
            This workspace is in billing recovery mode
          </p>
          <p className="mt-1 text-sm text-red-800">
            Full access is paused until payment is confirmed. Company data is
            currently retained until{' '}
            {formatBillingDate(subscription?.data_purge_scheduled_for)}.
          </p>
        </div>
      ) : null}

      <Tabs defaultValue="overview" className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="h-auto rounded-[20px] bg-slate-100 p-1">
            <TabsTrigger
              value="overview"
              className="rounded-2xl px-4 py-2.5 text-sm data-[state=active]:bg-white data-[state=active]:text-slate-950"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="plans"
              className="rounded-2xl px-4 py-2.5 text-sm data-[state=active]:bg-white data-[state=active]:text-slate-950"
            >
              Plans
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="rounded-2xl px-4 py-2.5 text-sm data-[state=active]:bg-white data-[state=active]:text-slate-950"
            >
              Invoices
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          {subscription ? (
            <CurrentPlanCard
              subscription={subscription}
              currentPackage={currentPackage}
              isTrialing={isTrialing}
              isCanceled={isCanceled}
              trialDaysRemaining={trialDaysRemaining}
              onManageBilling={handleManageBilling}
              // onCancelSubscription={() => setShowCancelModal(true)}
              onResumeSubscription={handleResumeSubscription}
              isLoading={isLoading}
            />
          ) : (
            <BillingSurface tone="inverse">
              <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-emerald-200/80">
                Subscription overview
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                Choose the plan that fits your team
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                Starter includes up to 5 user accounts, while Professional
                scales to 20 users per outlet. Open the Plans tab to start
                checkout.
              </p>
            </BillingSurface>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <BillingSurface className="h-full">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    Usage Limits
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Track plan capacity across outlets and user accounts from
                    the shared company roster.
                  </p>
                  {packageType === 'professional' && (
                    <p className="mt-2 text-xs font-medium text-emerald-700">
                      Professional user capacity scales at 20 users per outlet.
                    </p>
                  )}
                </div>
                <BillingStatusBadge
                  label={
                    usage &&
                    (usage.outlets.limit === null || usage.users.limit === null)
                      ? 'Flexible scale'
                      : 'Tracked'
                  }
                  tone="success"
                />
              </div>

              <div className="mt-5 space-y-3">
                {usage ? (
                  <>
                    <UsageProgress
                      label="Outlets"
                      used={usage.outlets.used}
                      limit={usage.outlets.limit}
                      icon={
                        <Building2 size={18} className="text-emerald-700" />
                      }
                    />
                    <UsageProgress
                      label="Users"
                      used={usage.users.used}
                      limit={usage.users.limit}
                      icon={<Users size={18} className="text-emerald-700" />}
                    />
                  </>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Usage data is still loading.
                  </div>
                )}
              </div>
            </BillingSurface>

            <BillingInvoiceSummary
              invoices={invoices}
              latestPaidInvoice={latestPaidInvoice}
              upcomingInvoice={upcomingInvoice}
            />
          </div>
        </TabsContent>

        <TabsContent value="plans" className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Available Plans
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Compare compact plan tiers and switch between monthly and yearly
                billing as your business grows.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedBillingCycle === 'annual' && (
                <BillingStatusBadge label="Save up to 17%" tone="success" />
              )}
              <BillingCycleToggle
                value={selectedBillingCycle}
                onChange={setSelectedBillingCycle}
              />
            </div>
          </div>

          <BillingSurface tone="tinted" className="px-4 py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {selectedBillingCycle === 'annual'
                    ? 'Yearly billing selected'
                    : selectedBillingCycle === 'semi_anually'
                      ? 'Semi-Annual billing selected'
                      : 'Monthly billing selected'}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {selectedBillingCycle === 'annual'
                    ? 'Starter and Professional save 16%, while Enterprise saves 17% when billed once every 12 months.'
                    : selectedBillingCycle === 'semi_anually'
                      ? 'Semi-annual billing gives you 6 months of uninterrupted access.'
                      : 'Monthly billing keeps pricing flexible with the same plan limits and features.'}
                </p>
              </div>
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-emerald-700">
                {selectedBillingCycle === 'annual'
                  ? 'Billed every 12 months'
                  : selectedBillingCycle === 'semi_anually'
                    ? 'Billed every 6 months'
                    : 'Billed every month'}
              </p>
            </div>
          </BillingSurface>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            {packages.map((pkg) => {
              const isActivePackage = activePackageId === pkg.id;
              const planGuard = getBillingPlanGuard(
                usage,
                pkg.id,
                planRateLimits,
              );
              const isCurrentSelection =
                pkg.id === subscription?.package &&
                selectedBillingCycle === currentBillingCycle;
              const isCycleUnavailable =
                !isCurrentSelection &&
                !isPackageCycleConfigured(pkg, selectedBillingCycle);
              const isPlanBlocked =
                pkg.id !== subscription?.package && !planGuard.isAllowed;
              const actionHint = isCycleUnavailable
                ? `${pkg.name} ${getBillingCycleActionLabel(selectedBillingCycle)} Not available for the selected billing period`
                : isPlanBlocked
                  ? planGuard.message || undefined
                  : undefined;

              return (
                <PackageCard
                  key={pkg.id}
                  packageId={pkg.id}
                  annualDiscountPercentage={pkg.annual_discount_percentage}
                  annualPrice={pkg.annual_price}
                  billingCycle={selectedBillingCycle}
                  name={pkg.name}
                  price={pkg.price}
                  description={pkg.description}
                  features={pkg.features}
                  isCurrent={pkg.id === subscription?.package}
                  featured={
                    pkg.id === 'professional' &&
                    pkg.id !== subscription?.package
                  }
                  actionLabel={
                    isActivePackage ? 'Working...' : getPlanActionLabel(pkg.id)
                  }
                  actionDisabled={
                    !companyId ||
                    isCycleUnavailable ||
                    isPlanBlocked ||
                    (activePackageId !== null && activePackageId !== pkg.id)
                  }
                  actionHint={actionHint}
                  onAction={() => handlePlanAction(pkg.id)}
                />
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <BillingHistoryTable invoices={invoices} />
        </TabsContent>
      </Tabs>

      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        size="sm"
      >
        <CancelSubscriptionContent
          onConfirm={handleCancelSubscription}
          onCancel={() => setShowCancelModal(false)}
          isLoading={isLoading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(planSwitchPreview)}
        onClose={() => setPlanSwitchPreview(null)}
        onConfirm={confirmPlanSwitch}
        isLoading={isSwitchingPlan}
        title="Confirm plan switch"
        confirmText="Switch plan"
        message={
          planSwitchPreview
            ? `Switching to ${packages.find((pkg) => pkg.id === planSwitchPreview.packageId)?.name || 'this plan'} (${getBillingCycleActionLabel(planSwitchPreview.billingCycle)}) will charge you ${formatMoney(planSwitchPreview.amountDue, { fromMinorUnits: true, currency: planSwitchPreview.currency })} today${planSwitchPreview.periodEnd ? `, covering your plan through ${formatBillingDate(new Date(planSwitchPreview.periodEnd * 1000).toISOString())}` : ''}.`
            : ''
        }
      />
    </div>
  );
};

export default BillingPage;
